import React, { useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { computeLeaders, trophyGap, compStatus, KISSED_THE_WATER_LINES, EMPTY_LEADERBOARD, randomFrom } from "@/lib/helpers";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Sprout, Fish } from "lucide-react";
import Ceremony, { hasSeenCeremony, markCeremonySeen } from "@/pages/Ceremony";
import Results from "@/pages/Results";

function TrophyCard({ icon, label, leaderName, leaderEmoji, leaderColor, stat, subtitle, testId }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-5 border-2 border-[#E8A838]/30 shadow-sm relative overflow-hidden"
      data-testid={testId}
    >
      <div className="flex items-center gap-4">
        {leaderName ? (
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-3xl shrink-0"
            style={{ background: (leaderColor || "#E8A838") + "33" }}
          >
            {leaderEmoji}
          </div>
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#FAF8F3] flex items-center justify-center text-3xl shrink-0">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B7D] mb-1">
            {label}
          </div>
          <div className="font-display text-xl font-black text-[#0A3D62] truncate">
            {leaderName || "Nobody yet"}
          </div>
          {subtitle && (
            <div className="text-sm text-[#556B7D] mt-0.5 truncate">{subtitle}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-3xl font-black text-[#E8A838]">{stat}</div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const { family, members, catches } = useApp();
  const leaders = useMemo(() => computeLeaders(catches, members), [catches, members]);
  const [emptyLine] = useState(() => randomFrom(KISSED_THE_WATER_LINES));

  const status = compStatus(family.tournament_start, family.tournament_end);

  // Post-comp mode: if the tournament end date has passed and there are catches,
  // route to Ceremony (first open) or Results (subsequent opens).
  const compClosed = !status.active && new Date() > new Date(family.tournament_end);
  const [ceremonyDismissed, setCeremonyDismissed] = useState(() => hasSeenCeremony(family));
  if (compClosed && catches.length > 0 && !ceremonyDismissed) {
    return (
      <Ceremony
        onDone={() => {
          markCeremonySeen(family);
          setCeremonyDismissed(true);
        }}
      />
    );
  }
  if (compClosed && catches.length > 0) {
    return <Results />;
  }

  const biggestGap = trophyGap(
    "biggest",
    leaders.biggest?.cm,
    leaders.topBiggest[1]?.catch?.length_cm,
  );
  const smallestGap = trophyGap("smallest", leaders.smallest?.cm, null);
  const mostCaughtSecond = leaders.standingsByCount[1]?.count;
  const mostCaughtGap = trophyGap("mostCaught", leaders.mostCaught?.count, mostCaughtSecond);

  return (
    <div className="px-5 pt-4 pb-4">
      {/* Header */}
      <div className="mb-6" data-testid="home-header">
        <div className="font-display text-3xl font-black text-[#0A3D62] leading-tight">
          {family.comp_name}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              status.active ? "bg-[#2D6A4F]" : "bg-[#556B7D]"
            }`}
          />
          <span className="text-sm font-semibold text-[#556B7D]" data-testid="comp-status">
            {status.label}
          </span>
        </div>
      </div>

      {/* Trophies */}
      <div className="space-y-3 mb-8" data-testid="trophy-cards">
        <TrophyCard
          testId="trophy-biggest"
          icon={<Trophy className="w-7 h-7 text-[#E8A838]" strokeWidth={2.5} />}
          label="🏆 Biggest"
          leaderName={leaders.biggest?.member?.name}
          leaderEmoji={leaders.biggest?.member?.emoji}
          leaderColor={leaders.biggest?.member?.color}
          stat={leaders.biggest ? `${leaders.biggest.cm.toFixed(0)}cm` : "—"}
          subtitle={biggestGap}
        />
        <TrophyCard
          testId="trophy-smallest"
          icon={<Sprout className="w-7 h-7 text-[#2D6A4F]" strokeWidth={2.5} />}
          label="🥄 Smallest"
          leaderName={leaders.smallest?.member?.name}
          leaderEmoji={leaders.smallest?.member?.emoji}
          leaderColor={leaders.smallest?.member?.color}
          stat={leaders.smallest ? `${leaders.smallest.cm.toFixed(0)}cm` : "—"}
          subtitle={smallestGap}
        />
        <TrophyCard
          testId="trophy-most-caught"
          icon={<Fish className="w-7 h-7 text-[#0A3D62]" strokeWidth={2.5} />}
          label="🎣 Most Caught"
          leaderName={leaders.mostCaught?.member?.name}
          leaderEmoji={leaders.mostCaught?.member?.emoji}
          leaderColor={leaders.mostCaught?.member?.color}
          stat={leaders.mostCaught ? leaders.mostCaught.count : "—"}
          subtitle={mostCaughtGap}
        />
      </div>

      {catches.length === 0 && (
        <div className="text-center py-14 px-8" data-testid="empty-leaderboard">
          <div className="text-6xl mb-4">🎣</div>
          <p className="text-[#556B7D] text-lg font-semibold">{EMPTY_LEADERBOARD}</p>
        </div>
      )}

      {/* Standings — Most Caught */}
      {leaders.standingsByCount.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-black text-[#0A3D62]">Most caught</h2>
            <span className="text-xs text-[#556B7D] font-semibold uppercase tracking-wider">
              {catches.length} total
            </span>
          </div>
          <div className="bg-white rounded-3xl p-2 border border-[#0A3D62]/5">
            <AnimatePresence>
              {leaders.standingsByCount.map((row, idx) => (
                <motion.div
                  layout
                  key={row.member.id}
                  className="flex items-center gap-3 px-3 py-3 rounded-2xl"
                  data-testid={`standing-most-${row.member.id}`}
                >
                  <div className="font-display text-lg font-black text-[#556B7D] w-6 text-center">
                    {idx + 1}
                  </div>
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                    style={{ background: row.member.color + "33" }}
                  >
                    {row.member.emoji}
                  </div>
                  <div className="flex-1 font-semibold text-[#0A3D62]">{row.member.name}</div>
                  <div className="font-display text-2xl font-black text-[#E8A838]">
                    {row.count}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Top 5 biggest */}
      {leaders.topBiggest.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-lg font-black text-[#0A3D62] mb-3">Biggest Fish — Top 5</h2>
          <div className="bg-white rounded-3xl p-2 border border-[#0A3D62]/5">
            {leaders.topBiggest.map((row, idx) => (
              <div
                key={row.catch.id}
                className="flex items-center gap-3 px-3 py-3 rounded-2xl"
                data-testid={`standing-biggest-${row.catch.id}`}
              >
                <div className="font-display text-lg font-black text-[#556B7D] w-6 text-center">
                  {idx + 1}
                </div>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                  style={{ background: (row.member?.color || "#556B7D") + "33" }}
                >
                  {row.member?.emoji || "🎣"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0A3D62] truncate">
                    {row.member?.name || "Unknown"}
                  </div>
                  <div className="text-xs text-[#556B7D] truncate">{row.catch.species || "Other"}</div>
                </div>
                <div className="font-display text-2xl font-black text-[#E8A838]">
                  {row.catch.length_cm.toFixed(0)}
                  <span className="text-xs text-[#556B7D] ml-1">cm</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wooden spoon — kissed the water */}
      {leaders.anglersWithoutCatches.length > 0 && catches.length > 0 && (
        <div className="mb-4">
          <h2 className="font-display text-lg font-black text-[#556B7D] mb-3">Kissed the Water</h2>
          <div className="bg-[#0A3D62]/5 rounded-3xl p-2 border border-dashed border-[#0A3D62]/15">
            {leaders.anglersWithoutCatches.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 px-3 py-3"
                data-testid={`standing-none-${m.id}`}
              >
                <div className="text-lg w-6 text-center">🥄</div>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-xl opacity-60"
                  style={{ background: m.color + "33" }}
                >
                  {m.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0A3D62] truncate">{m.name}</div>
                  <div className="text-xs text-[#556B7D] italic truncate">{emptyLine}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
